import { ref } from "vue";
import type { Ref } from "vue";
import {
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
  ListDeliveryCapOrdersQuery,
  ListDeliveryCapOrdersQueryVariables,
  ListDeliveryCapOrdersDocument,
  ListDeliveryGroupCapOrdersQuery,
  ListDeliveryGroupCapOrdersQueryVariables,
  ListDeliveryGroupCapOrdersDocument,
  type DeliveryCapOrders,
  type DeliveryGroupCapOrders,
  type DeliveryCapOrdersListInput,
  type DeliveryGroupCapOrdersListInput,
  type ShopListInput,
} from "@/generated/graphql";
import {
  localStorageGetCustomerCode,
  localStorageGetChainCode,
} from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getShopList } from "@/common/graphql/shop";
import { WEEKS } from "../constants";
import type {
  ListData,
  ListGroupData,
  TableHeaderItem,
  AreaListItem,
  ChainSelectValue,
  ShopSelectValue,
  ShopItem,
  DeliveryGroupSelectValue,
  DeliveryGroupIdItem,
} from "../types";

/**
 * テーブルヘッダー管理、キャップデータ取得・変換、日付範囲管理
 */
export const useDeliveryCapSettingTable = (params: {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  processFlag: Ref<number>;
  selectShopFlg: Ref<boolean>;
  selectAreaFlg: Ref<boolean>;
  selectGroupFlg: Ref<boolean>;
  selectGroupIdFlg: Ref<boolean>;
  setIdFlg: Ref<boolean>;
  mode: Ref<string>;
  chainViewFlag: boolean;
  wordList: Record<string, string>;
  chainSelectValues: Ref<ChainSelectValue>;
  shopSelectValues: Ref<ShopSelectValue>;
  areaSelectValues: Ref<AreaListItem | null>;
  areaListItems: Ref<(AreaListItem | null)[]>;
  shopListItems: Ref<ShopItem[]>;
  shopListInput: ShopListInput;
  deliveryGroupSelectValues: Ref<DeliveryGroupSelectValue>;
  deliveryGroupIdSelectValues: Ref<DeliveryGroupIdItem[]>;
  tableHeaders: Ref<TableHeaderItem[]>;
  displayDateFrom: Ref<string | null>;
  displayDateTo: Ref<string | null>;
  listDataArray: Ref<ListData[]>;
  listGroupDataArray: Ref<ListGroupData[]>;
  deliveryCapOrdersListInput: DeliveryCapOrdersListInput;
  deliveryGroupCapOrdersListInput: DeliveryGroupCapOrdersListInput;
  areaParentData: Map<string, number>;
  mappedListData: Map<string, ListData[]>;
  mappedListGroupData: Map<string, ListGroupData[]>;
}) => {
  const {
    errdialog,
    errMessage,
    overlay,
    processFlag,
    selectShopFlg,
    selectAreaFlg,
    selectGroupFlg,
    selectGroupIdFlg,
    setIdFlg,
    mode,
    chainViewFlag,
    wordList,
    chainSelectValues,
    shopSelectValues,
    areaSelectValues,
    areaListItems,
    shopListItems,
    shopListInput,
    deliveryGroupSelectValues,
    deliveryGroupIdSelectValues,
    tableHeaders,
    displayDateFrom,
    displayDateTo,
    listDataArray,
    listGroupDataArray,
    deliveryCapOrdersListInput,
    deliveryGroupCapOrdersListInput,
    areaParentData,
    mappedListData,
    mappedListGroupData,
  } = params;

  // キャップデータ（Table 内部のみ使用）
  const deliveryCapItems = ref<(DeliveryCapOrders | null | undefined)[]>([]);
  const deliveryGroupCapItems = ref<(DeliveryGroupCapOrders | null | undefined)[]>([]);
  const listAreaDataArray = ref<ListGroupData[]>([]);

  const fieldName = (key: string): string => {
    return wordList[key] || key;
  };

  /**
   * 時間文字列を "HH:MM" 形式にフォーマットする
   * 例: "9:00:00" → "09:00", "20:00:00" → "20:00"
   */
  const formatTimeHHMM = (time: string): string => {
    const parts = time.split(":");
    return parts[0].padStart(2, "0") + ":" + parts[1];
  };

  const formatDateValue = (value: unknown): string | null => {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    if (typeof value === "string") {
      return value.length >= 10 ? value.slice(0, 10) : value;
    }
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    if (typeof value === "number") {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return null;
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return null;
  };

  const dateDataKey = (dateValue: string): string => {
    return (
      "date_data_" +
      dateValue.substring(0, 4) +
      dateValue.substring(5, 7) +
      dateValue.substring(8, 10)
    );
  };

  const capCellProps = ({
    column,
    item,
  }: {
    column: { key: string };
    item: Record<string, unknown>;
  }): Record<string, string | number> => {
    const baseClass = "px-0 py-0 text-center";
    const groupFlag = (item as { group_flag?: boolean }).group_flag;
    const areaFlag = (item as { area_flag?: boolean }).area_flag;
    const isBlankRow = groupFlag === false && areaFlag === false;

    if (isBlankRow) {
      if (column.key === "delivery_id_time") {
        const dateCount = tableHeaders.value.length || 1;
        return {
          class: `${baseClass} blank_line`,
          colspan: dateCount,
          style:
            "border-right: 1px solid #eeeeee; border-bottom: 1px solid #eeeeee; height: 24px; background-color: #f5f5f5;",
        };
      }
      return { style: "display: none;" };
    }

    if (column.key === "delivery_id_time") {
      const height = groupFlag ? "80px" : "64px";
      return {
        class: baseClass,
        style: `border-bottom: 1px solid #eeeeee; height: ${height};`,
      };
    }

    const dataHeight = groupFlag ? "80px" : "64px";
    const dateCell = item[column.key] as { week_date?: number } | undefined;
    const weekClass =
      dateCell?.week_date === 6
        ? " cap-cell-sat"
        : dateCell?.week_date === 0
          ? " cap-cell-sun"
          : "";
    return {
      class: `${baseClass}${weekClass}`,
      style: `border-right: 1px solid #eeeeee; border-bottom: 1px solid #eeeeee; height: ${dataHeight};`,
    };
  };

  /**
   * エリア名を取得
   */
  const getAreaName = (area_code: string): string => {
    let area_name = "";
    areaListItems.value.forEach((value) => {
      if (
        value !== null &&
        value !== undefined &&
        value.area_code === area_code
      ) {
        area_name = value.area_name;
      }
    });
    return area_name;
  };

  /**
   * 表示日数(TO)が(FROM)以上に指定されているかチェック
   */
  const isDateToMoreDateFrom = (): boolean => {
    if (displayDateFrom.value == null || displayDateTo.value == null) {
      return true;
    }
    return displayDateFrom.value <= displayDateTo.value;
  };

  /**
   * テーブルヘッダーを日付範囲から生成する
   */
  const generateTableHeaders = (): void => {
    tableHeaders.value = [];
    tableHeaders.value.push({
      title: "配達便・時間",
      align: "center",
      sortable: false,
      key: "delivery_id_time",
      class: "text-center",
      width: "160px",
    });

    if (!displayDateFrom.value || !displayDateTo.value) {
      return;
    }

    const start = new Date(displayDateFrom.value);
    const end = new Date(displayDateTo.value);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const w = d.getDay();
      let headerColor = "";
      if (w === 0) headerColor = "red";
      if (w === 6) headerColor = "blue";
      const colValue =
        "date_data_" +
        d.getFullYear() +
        String(d.getMonth() + 1).padStart(2, "0") +
        String(d.getDate()).padStart(2, "0");

      tableHeaders.value.push({
        title:
          String(d.getMonth() + 1).padStart(2, "0") +
          "/" +
          String(d.getDate()).padStart(2, "0") +
          " (" +
          WEEKS[w] +
          ")",
        align: "center",
        sortable: false,
        key: colValue,
        width: "160px",
        ...(headerColor ? { headerProps: { style: `color: ${headerColor}` } } : {}),
      });
    }
  };

  /**
   * 絞り込み検索 @change
   */
  const onChangeDate = async (): Promise<void> => {
    if (!displayDateFrom.value || !displayDateTo.value) {
      return;
    }

    if (selectShopFlg.value || selectAreaFlg.value) {
      await getCapOrders();
    }

    if (mode.value === "group" && (selectGroupFlg.value || selectGroupIdFlg.value)) {
      await getGroupCapOrders();
    }
  };

  const onSelectDisplayDateFrom = async (value: unknown): Promise<void> => {
    displayDateFrom.value = formatDateValue(value);
    await onChangeDate();
  };

  const onSelectDisplayDateTo = async (value: unknown): Promise<void> => {
    displayDateTo.value = formatDateValue(value);
    await onChangeDate();
  };

  /**
   * エリア便キャップ一覧取得
   */
  const getCapOrders = async (): Promise<void> => {
    if (!isDateToMoreDateFrom()) {
      return;
    }

    overlay.value = true;
    processFlag.value = 0;

    // graphql接続
    const apolloClient = await generateApolloClient();

    /**
     * エリアデータ取得処理
     */
    const listArea = await apolloClient.query<
      ListDeliveryAreaQuery,
      ListDeliveryAreaQueryVariables
    >({
      query: ListDeliveryAreaDocument,
      variables: {
        chain_code: chainSelectValues.value.chain_code,
        customer_code: localStorageGetCustomerCode(),
        shop_code: shopSelectValues.value.shop_code,
      },
      fetchPolicy: "no-cache",
    });

    if (
      listArea.data.listDeliveryArea == null ||
      listArea.data.listDeliveryArea.length === 0
    ) {
      selectShopFlg.value = false;
      selectAreaFlg.value = false;
      overlay.value = false;
      return;
    }

    areaListItems.value = listArea.data.listDeliveryArea;

    deliveryCapOrdersListInput.display_date_start = displayDateFrom.value || "";
    deliveryCapOrdersListInput.display_date_end = displayDateTo.value || "";
    deliveryCapOrdersListInput.chain_code = chainSelectValues.value.chain_code;
    deliveryCapOrdersListInput.shop_code = shopSelectValues.value.shop_code;
    deliveryCapOrdersListInput.area_code =
      areaSelectValues.value?.area_code || "";

    // 店舗の全エリアの情報を取得
    const ListDeliveryCapOrders = await apolloClient.query<
      ListDeliveryCapOrdersQuery,
      ListDeliveryCapOrdersQueryVariables
    >({
      query: ListDeliveryCapOrdersDocument,
      variables: {
        input: deliveryCapOrdersListInput,
      },
      fetchPolicy: "no-cache",
    });

    deliveryCapItems.value =
      ListDeliveryCapOrders.data.listDeliveryCapOrders || [];
    listDataArray.value = [];

    if (deliveryCapItems.value.length === 0) {
      selectShopFlg.value = false;
      selectAreaFlg.value = false;
      overlay.value = false;
      return;
    }

    deliveryCapItems.value.forEach((value) => {
      if (
        value &&
        value.delivery_cap_detail &&
        value.delivery_cap_detail.length > 0
      ) {
        areaParentData.set(value.area_code, value.has_parent_group);

        value.delivery_cap_detail.forEach((subValue) => {
          if (!subValue) return;
          let set = false;
          let target_date = new Date();
          let pro_str = "";
          let tmp_data = {
            receipt_count: 0,
            cap_count: 0,
            week_date: 0,
          };

          // 既存行を探して更新
          for (let i = 0; i < listDataArray.value.length; i++) {
            const row = listDataArray.value[i];
            if (
              subValue.delivery_id === row.delivery_id &&
              value.area_code === row.area_code
            ) {
              pro_str = dateDataKey(subValue.delivery_date);
              target_date = new Date(subValue.delivery_date);
              tmp_data = {
                receipt_count: subValue.orders,
                cap_count: subValue.cap,
                week_date: target_date.getDay(),
              };
              row[pro_str] = tmp_data;
              set = true;
              break;
            }
          }

          // 新規行を追加
          if (!set) {
            pro_str = dateDataKey(subValue.delivery_date);
            target_date = new Date(subValue.delivery_date);
            tmp_data = {
              receipt_count: subValue.orders,
              cap_count: subValue.cap,
              week_date: target_date.getDay(),
            };
            listDataArray.value.push({
              area_code: value.area_code,
              area_name: value.area_name,
              delivery_id: subValue.delivery_id,
              has_parent_group: subValue.has_parent_group,
              delivery_time:
                formatTimeHHMM(subValue.delivery_start_time) +
                "~" +
                formatTimeHHMM(subValue.delivery_end_time),
              [pro_str]: tmp_data,
            } as ListData);
          }
        });
      }
    });

    // エリアをキーにした形にマップ変換
    mappedListData.clear();
    if (areaSelectValues.value?.area_code) {
      const filteredRows = listDataArray.value.filter(
        (row) => row.area_code === areaSelectValues.value?.area_code
      );
      if (filteredRows.length > 0) {
        mappedListData.set(areaSelectValues.value.area_code, filteredRows);
      }
    } else {
      areaListItems.value.forEach((area) => {
        if (area) {
          mappedListData.set(area.area_code, []);
        }
      });
      listDataArray.value.forEach((row) => {
        const arr = mappedListData.get(row.area_code) || [];
        arr.push(row);
        mappedListData.set(row.area_code, arr);
      });
    }

    // ヘッダー再生成
    generateTableHeaders();

    if (!areaSelectValues.value?.area_code) {
      selectShopFlg.value = true;
      selectAreaFlg.value = false;
    } else {
      selectShopFlg.value = false;
      selectAreaFlg.value = true;
    }

    overlay.value = false;
  };

  /**
   * 配達グループ便データ取得
   */
  const getGroupCapOrders = async (): Promise<void> => {
    if (!isDateToMoreDateFrom()) {
      return;
    }
    if (!deliveryGroupSelectValues.value.group_code) {
      mappedListGroupData.clear();
      return;
    }

    overlay.value = true;
    processFlag.value = 0;

    // graphql接続
    const apolloClient = await generateApolloClient();

    selectGroupFlg.value = true;
    deliveryGroupCapOrdersListInput.group_code =
      deliveryGroupSelectValues.value.group_code;
    deliveryGroupCapOrdersListInput.display_date_start =
      displayDateFrom.value || "";
    deliveryGroupCapOrdersListInput.display_date_end =
      displayDateTo.value || "";
    let date_count = 0;
    let date_set = false;

    if (!chainViewFlag) {
      deliveryGroupCapOrdersListInput.chain_code = localStorageGetChainCode();
    }

    // グループキャップ、紐づく店舗キャップ情報を取得
    const ListDeliveryGroupCapOrders = await apolloClient.query<
      ListDeliveryGroupCapOrdersQuery,
      ListDeliveryGroupCapOrdersQueryVariables
    >({
      query: ListDeliveryGroupCapOrdersDocument,
      variables: { input: deliveryGroupCapOrdersListInput },
      fetchPolicy: "no-cache",
    });

    // 店舗名を取得するため一覧を取得
    shopListInput.chain_code = chainSelectValues.value.chain_code;
    shopListItems.value = await getShopList(shopListInput);

    if (
      ListDeliveryGroupCapOrders.data.listDeliveryGroupCapOrders!.length > 0
    ) {
      deliveryGroupCapItems.value =
        ListDeliveryGroupCapOrders.data.listDeliveryGroupCapOrders!;
      listGroupDataArray.value = [];
      listAreaDataArray.value = [];

      deliveryGroupCapItems.value.forEach((value) => {
        if (!value) return;
        let set = false;
        let target_date = new Date();
        let pro_str = "";
        let tmp_data = {
          group_orders: 0,
          group_cap: 0,
          group_dist_cap: 0,
          week_date: 0,
        };
        let check_delivery_id = false;

        if (!setIdFlg.value) {
          //グループ便に条件が入っていて、そのグループ便条件に該当しない場合はcontinueする
          if (deliveryGroupIdSelectValues.value.length > 0) {
            deliveryGroupIdSelectValues.value.forEach((id_value) => {
              if (
                String(value.group_delivery_id) ===
                id_value.group_delivery_id
              ) {
                check_delivery_id = true;
              }
            });
            if (!check_delivery_id) return;
          } else {
            return;
          }
        } else {
          //デフォルト状態のため、すべてのグループ便がチェックされた状態にする
          deliveryGroupIdSelectValues.value.forEach((id_value) => {
            if (
              String(value.group_delivery_id) === id_value.group_delivery_id
            ) {
              check_delivery_id = true;
            }
          });
          if (!check_delivery_id) {
            deliveryGroupIdSelectValues.value.push({
              group_delivery_id: String(value.group_delivery_id),
              group_delivery_id_name:
                String(value.group_delivery_id) + "便",
            });
          }
        }

        //自由分配数計算用 エリア・便キャップ
        let area_cap_count = 0;

        if (
          value.delivery_cap_orders &&
          value.delivery_cap_orders.length > 0
        ) {
          value.delivery_cap_orders.forEach((area_value) => {
            if (!area_value) return;
            area_value.delivery_cap_detail?.forEach((area_detail_value) => {
              if (!area_detail_value) return;
              let area_set = false;
              let area_target_date = new Date();
              let area_pro_str = "";
              let area_tmp_data = { orders: 0, cap: 0, week_date: 0 };

              for (let i = 0; i < listAreaDataArray.value.length; i++) {
                const row = listAreaDataArray.value[i];
                if (
                  area_value.area_code === row.area_code &&
                  area_detail_value.delivery_id === row.delivery_id &&
                  value.group_delivery_id === row.group_delivery_id
                ) {
                  area_pro_str = dateDataKey(value.delivery_date);
                  area_target_date = new Date(value.delivery_date);
                  area_tmp_data = {
                    orders: area_detail_value.orders,
                    cap: area_detail_value.cap,
                    week_date: area_target_date.getDay(),
                  };
                  row[area_pro_str] = area_tmp_data;
                  area_set = true;
                  break;
                }
              }

              if (!area_set) {
                area_pro_str = dateDataKey(value.delivery_date);
                area_target_date = new Date(value.delivery_date);
                area_tmp_data = {
                  orders: area_detail_value.orders,
                  cap: area_detail_value.cap,
                  week_date: area_target_date.getDay(),
                };
                let shop_name = "";
                shopListItems.value.forEach((shop) => {
                  if (shop && shop.shop_code === area_value.shop_code) {
                    shop_name = shop.name || "";
                  }
                });
                listAreaDataArray.value.push({
                  group_delivery_id: value.group_delivery_id,
                  group_flag: false,
                  area_flag: true,
                  shop_code: area_value.shop_code,
                  shop_name,
                  area_code: area_value.area_code,
                  area_name: area_value.area_name,
                  delivery_id: area_detail_value.delivery_id,
                  delivery_time:
                    formatTimeHHMM(area_detail_value.delivery_start_time) +
                    "~" +
                    formatTimeHHMM(area_detail_value.delivery_end_time),
                  [area_pro_str]: area_tmp_data,
                });
              }

              area_cap_count += area_detail_value.cap;
            });
          });
        }

        for (let i = 0; i < listGroupDataArray.value.length; i++) {
          const row = listGroupDataArray.value[i];
          if (value.group_delivery_id === row.group_delivery_id) {
            if (!date_set) date_count++;
            pro_str = dateDataKey(value.delivery_date);
            target_date = new Date(value.delivery_date);
            tmp_data = {
              group_orders: value.group_orders,
              group_cap: value.group_cap,
              group_dist_cap: value.group_cap - area_cap_count,
              week_date: target_date.getDay(),
            };
            row[pro_str] = tmp_data;
            set = true;
            break;
          }
        }

        if (!set) {
          if (!date_set) date_count++;
          pro_str = dateDataKey(value.delivery_date);
          target_date = new Date(value.delivery_date);
          tmp_data = {
            group_orders: value.group_orders,
            group_cap: value.group_cap,
            group_dist_cap: value.group_cap - area_cap_count,
            week_date: target_date.getDay(),
          };
          listGroupDataArray.value.push({
            group_delivery_id: value.group_delivery_id,
            group_flag: true,
            area_flag: false,
            [pro_str]: tmp_data,
          });
        }

        date_set = true;
      });

      // グループをキーにした形に変換
      mappedListGroupData.clear();
      listGroupDataArray.value.forEach((row) => {
        const arr =
          mappedListGroupData.get(String(row.group_delivery_id)) || [];
        // 紐づくエリアを追加
        listAreaDataArray.value.forEach((areaRow) => {
          if (areaRow.group_delivery_id === row.group_delivery_id) {
            arr.push(areaRow);
          }
        });
        // 空行用
        arr.push({
          group_flag: false,
          area_flag: false,
          group_delivery_id: 0,
          date_count: date_count + 1,
        });
        arr.push(row);
        mappedListGroupData.set(String(row.group_delivery_id), arr);
      });
    } else {
      overlay.value = false;
      mappedListGroupData.clear();
    }

    // ヘッダー再生成
    generateTableHeaders();

    overlay.value = false;
    setIdFlg.value = false;
  };

  return {
    getCapOrders,
    getGroupCapOrders,
    onChangeDate,
    onSelectDisplayDateFrom,
    onSelectDisplayDateTo,
    dateDataKey,
    formatDateValue,
    capCellProps,
    fieldName,
    getAreaName,
    isDateToMoreDateFrom,
  };
};
